import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import toast, { Toaster, ToastBar } from 'react-hot-toast';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ProtectedRoute from './components/ProtectedRoute';
import authService from './services/authService';
import orgService from './services/orgService';
import fcmService from './services/fcmService';
import './App.css';

const App: React.FC = () => {
  // Fetch organization_id and initialize FCM on app initialization if user is authenticated
  useEffect(() => {
    const initializeApp = async (): Promise<void> => {
      // Check if user is authenticated
      if (authService.isAuthenticated()) {
        // Check if organization_id is missing
        if (!orgService.hasOrganizationId()) {
          try {
            await orgService.fetchUserOrganization();
            console.log('Organization ID fetched on app initialization');
          } catch (error) {
            // Log error but don't block app - organization_id can be fetched when needed
            console.error('Failed to fetch organization ID on app initialization:', error);
          }
        }

        // Initialize FCM only for admin users
        if (fcmService.shouldInitialize()) {
          try {
            const token = await fcmService.initializeAndRegister();
            if (token) {
              console.log('FCM initialized and registered on app initialization');
              
              // Set up foreground message handler
              fcmService.onMessage((payload) => {
                const title = payload.notification?.title || payload.data?.title || 'File received';
                const body = payload.notification?.body || payload.data?.body || 'A new file has been received.';
                const message = body ? body : title;
                console.log(body)
                // In-app toast – stays until user closes via cross button
                toast.success(message, {
                  duration: Infinity,
                });
                // Browser notification when app is in foreground
                if (payload.notification && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                  new Notification(title, {
                    body,
                    icon: payload.notification.icon || '/logo192.png',
                    badge: '/logo192.png',
                    tag: payload.data?.tag || 'notification',
                    data: payload.data,
                  });
                }
              });
            }
          } catch (error) {
            // Log error but don't block app - FCM is optional
            console.error('Failed to initialize FCM on app initialization:', error);
          }
        } else {
          console.log('FCM initialization skipped: User is not an admin or browser does not support FCM');
        }
      }
    };

    initializeApp();
  }, []);

  return (
    <Router>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: '#fff',
            color: '#1f2937',
            border: '1px solid #e5e7eb',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
          success: {
            iconTheme: {
              primary: '#22c55e',
              secondary: '#fff',
            },
          },
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                {icon}
                <span style={{ flex: 1 }}>{message}</span>
                {t.type !== 'loading' && (
                  <button
                    type="button"
                    aria-label="Close notification"
                    onClick={() => toast.dismiss(t.id)}
                    style={{
                      padding: '4px 6px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#6b7280',
                      fontSize: '20px',
                      lineHeight: 1,
                      flexShrink: 0,
                    }}
                  >
                    ×
                  </button>
                )}
              </span>
            )}
          </ToastBar>
        )}
      </Toaster>
      <div className="App">
        <main className="App__content">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route 
              path="/dashboard/*" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </main>
        <footer className="App__footer">
          Copyright ©2025 Royal Cyber Inc.
        </footer>
      </div>
    </Router>
  );
}

export default App;